(() => {
  if (window.covaltBackorderCheckout) return;
  window.covaltBackorderCheckout = true;

  const flagKey = '_covalt_backorder';
  const dateKey = '_covalt_dispatch_date';
  const routes = window.routes || {};
  const cartUrl = new URL(routes.cartUrl || '/cart', window.location.origin);
  const changeUrl = new URL(routes.cartChangeUrl || '/cart/change.js', window.location.origin);
  changeUrl.pathname = `${changeUrl.pathname.replace(/\/$/, '').replace(/\.js$/, '')}.js`;
  const checkoutPath = cartUrl.pathname.replace(/cart\/?$/, 'checkout');
  const bypassForms = new WeakSet();
  let pending = false;
  let feedback;

  function validDate(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const parsed = new Date(`${value}T00:00:00Z`);
    return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  }

  function propertiesOf(item) {
    if (Array.isArray(item.properties)) return Object.fromEntries(item.properties);
    return { ...(item.properties || {}) };
  }

  function desiredProperties(item) {
    const properties = propertiesOf(item);
    if (item.dispatch_date !== null) {
      if (!validDate(item.dispatch_date)) throw new Error('Invalid dispatch date');
      if (properties[flagKey] === 'true' && properties[dateKey] === item.dispatch_date) return null;
      return { ...properties, [flagKey]: 'true', [dateKey]: item.dispatch_date };
    }
    if (!properties[flagKey] && !properties[dateKey]) return null;
    delete properties[flagKey];
    delete properties[dateKey];
    return Object.keys(properties).length ? properties : { [flagKey]: '', [dateKey]: '' };
  }

  async function requestJson(url, options = {}) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, {
        ...options,
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...options.headers }
      });
      if (!response.ok) throw new Error('Unable to verify cart');
      return await response.json();
    } finally {
      window.clearTimeout(timeout);
    }
  }

  async function synchronize() {
    const snapshotUrl = new URL(cartUrl);
    snapshotUrl.searchParams.set('view', 'covalt-backorder');
    let maximumUpdates = 100;
    for (let attempt = 0; attempt <= maximumUpdates; attempt += 1) {
      const snapshot = await requestJson(snapshotUrl, { headers: { Accept: 'text/html' } });
      if (!snapshot || !Array.isArray(snapshot.items)) throw new Error('Invalid cart snapshot');
      if (attempt === 0) maximumUpdates = Math.min(100, snapshot.items.length + 5);
      let update;
      for (const item of snapshot.items) {
        if (!item || typeof item.key !== 'string' || !item.key || !Number.isSafeInteger(item.quantity) || item.quantity < 1) {
          throw new Error('Invalid cart item');
        }
        const properties = desiredProperties(item);
        if (properties) {
          update = { id: item.key, quantity: item.quantity, properties };
          break;
        }
      }
      if (!update) return;
      if (attempt === maximumUpdates) throw new Error('Cart synchronization did not settle');
      await requestJson(changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(update)
      });
    }
    throw new Error('Cart synchronization did not settle');
  }

  function showFeedback(context, failed) {
    feedback?.remove();
    feedback = document.createElement('div');
    feedback.dataset.covaltCheckoutFeedback = '';
    feedback.setAttribute('role', failed ? 'alert' : 'status');
    const message = document.createElement('p');
    message.textContent = failed
      ? 'No pudimos confirmar la fecha de envío de tus productos. Reintenta para continuar al pago.'
      : 'Confirmando las fechas de envío de tus productos…';
    feedback.append(message);
    if (failed) {
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'button button--secondary';
      retry.textContent = 'Reintentar';
      retry.addEventListener('click', () => continueCheckout(context));
      feedback.append(retry);
    }
    const anchor = context.control?.isConnected ? context.control : context.form;
    if (anchor?.isConnected) anchor.insertAdjacentElement('afterend', feedback);
    else document.body.append(feedback);
  }

  function resume(context) {
    if (!context.form) {
      window.location.assign(context.url.href);
      return;
    }
    if (!context.form.isConnected) throw new Error('Cart form was replaced');
    if (typeof context.form.requestSubmit === 'function') {
      bypassForms.add(context.form);
      context.form.requestSubmit(context.control || undefined);
      bypassForms.delete(context.form);
      return;
    }
    const checkout = document.createElement('input');
    checkout.type = 'hidden';
    checkout.name = 'checkout';
    checkout.value = context.control?.value || '';
    context.form.append(checkout);
    HTMLFormElement.prototype.submit.call(context.form);
    checkout.remove();
  }

  async function continueCheckout(context) {
    if (pending) return;
    pending = true;
    const control = context.control;
    const disabled = control && 'disabled' in control ? control.disabled : undefined;
    const busy = control?.getAttribute('aria-busy');
    control?.setAttribute('aria-busy', 'true');
    if (disabled !== undefined) control.disabled = true;
    showFeedback(context, false);
    try {
      await synchronize();
      if (disabled !== undefined) control.disabled = disabled;
      resume(context);
      feedback?.remove();
    } catch {
      showFeedback(context, true);
    } finally {
      if (disabled !== undefined) control.disabled = disabled;
      if (control) {
        if (busy === null) control.removeAttribute('aria-busy');
        else control.setAttribute('aria-busy', busy);
      }
      pending = false;
    }
  }

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (bypassForms.has(form)) return;
    const action = new URL(form.action, window.location.origin);
    if (action.origin !== cartUrl.origin || action.pathname.replace(/\/$/, '') !== cartUrl.pathname.replace(/\/$/, '')) return;
    const control = event.submitter || form.querySelector('[name="checkout"]');
    if (!control || control.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    continueCheckout({ form, control });
  }, true);

  document.addEventListener('click', (event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!anchor || anchor.hasAttribute('download') || anchor.target && anchor.target !== '_self') return;
    const url = new URL(anchor.href, window.location.origin);
    const pathname = url.pathname.replace(/\/$/, '');
    if (url.origin !== window.location.origin || pathname !== '/checkout' && pathname !== checkoutPath.replace(/\/$/, '')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    continueCheckout({ control: anchor, url });
  }, true);
})();
